import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faUserTag } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "../scss/index.scss";
import { apiRequestLimits, changePublicName, setAccountType } from "../store/actions";
import { Helmet } from "react-helmet";
import TwoFactorSection from "../components/globalView/TwoFactorSection";
import { useAccountType } from "../hooks/useAccountType";
import { apiErrorMessage } from "../utils/partnerApi";

const ACCOUNT_TYPE_LABEL = { partner: "Partner account", kol: "KOL / creator account", agency: "Agency account" };

const MODE_TOAST = {
  partner: "Partner mode on — API and widget tools are back.",
  kol: "KOL mode on — campaigns and stats are in the menu.",
  agency: "Agency mode on — invite KOL accounts and follow their stats from the Agency menu.",
};
const MODE_HELP = {
  partner: "Partner mode is the classic integrator dashboard with API keys, widget and whitelisting.",
  kol: "KOL mode shows Referral & Campaigns and Campaign Stats; the API, Widget and Whitelisting tools are hidden (their pages still work by URL).",
  agency: "Agency mode adds the Agency menu: invite KOL accounts, and once they accept, see their campaigns and stats read-only. Your own data and earnings are untouched.",
};

// Access level self-service (KOL_CAMPAIGNS_AGENCY_PLAN.md §7.1 / §7.3 B): every mode can
// switch to every other mode; links an agency made stay in place while it is in another mode.
const AccountModeSection = () => {
  const dispatch = useDispatch();
  const accountType = useAccountType();
  const [busy, setBusy] = useState(false);

  const choose = (next) => {
    if (busy || next === accountType) return;
    if (accountType === "agency" && !window.confirm("Leave Agency mode? Your linked KOL accounts stay linked and come back when you switch to Agency again.")) return;
    setBusy(true);
    dispatch(setAccountType(next))
      .then(() => toast.success(MODE_TOAST[next]))
      .catch((e) => toast.error(apiErrorMessage(e, "Could not change the account mode.")))
      .finally(() => setBusy(false));
  };

  return (
    <div className="panel" style={{ marginBottom: 24, padding: 18 }}>
      <h3 className="secH" style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 10 }}>
        <FontAwesomeIcon icon={faUserTag} /> Account mode
      </h3>
      <p className="secLead" style={{ marginTop: 0 }}>
        Pick what the dashboard is for. Nothing about your data or earnings changes — only the menu and the tools you see.
      </p>
      <div className="seg" style={{ display: "inline-flex", flexWrap: "wrap" }}>
        <button type="button" className={`segB${accountType === "partner" ? " on" : ""}`} disabled={busy} onClick={() => choose("partner")}>
          Partner · API &amp; widget
        </button>
        <button type="button" className={`segB${accountType === "kol" ? " on" : ""}`} disabled={busy} onClick={() => choose("kol")}>
          KOL · referral campaigns
        </button>
        <button type="button" className={`segB${accountType === "agency" ? " on" : ""}`} disabled={busy} onClick={() => choose("agency")}>
          Agency · manage KOLs
        </button>
      </div>
      <p className="fldHelp" style={{ margin: "12px 0 0" }}>
        {MODE_HELP[accountType] || MODE_HELP.partner}
        {busy && <> <FontAwesomeIcon icon={faSpinner} spin /></>}
      </p>
    </div>
  );
};

const Account = () => {
  const dispatch = useDispatch();
  const accountType = useAccountType();

  const [partnerPublicName, setPartnerPublicName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const user = useSelector((state) => state.userReducer.user);
  const isLoading = useSelector((state) => state.userReducer.loading);

  // Load partner public name on mount
  useEffect(() => {
    if (user?.response?.partnerPublicName) {
      setPartnerPublicName(user.response.partnerPublicName);
    }
  }, [user]);

  const handleSubmit = () => {
    if (!partnerPublicName || partnerPublicName.trim() === "") {
      setErrorMessage("Please enter a public name");
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return;
    }

    dispatch(changePublicName(partnerPublicName))
      .then(() => {
        setSuccessMessage("Account information updated successfully!");
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      })
      .catch((error) => {
        setErrorMessage(error || "Failed to update public name");
        setTimeout(() => {
          setErrorMessage("");
        }, 3000);
      });
  };

  useEffect(() => {
    dispatch(apiRequestLimits());
  }, [dispatch]);

  const displayName =
    user?.response?.partnerPublicName || user?.response?.partnerName;
  const initials = (displayName || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      <Helmet>
        <title>Account | Instaswap Partners</title>
      </Helmet>

      <div>
        <div className="secHdr">
          <h2 className="secTitle">Account</h2>
          <p className="secLead">
            Manage your profile details and account security.
          </p>
        </div>

        <div style={{ maxWidth: 900 }}>
          <div className="panel" style={{ marginBottom: 24, padding: 18 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                marginBottom: 20,
              }}
            >
              <div className="acAvatar" style={{ margin: 0 }}>
                {user?.response?.partnerImage ? (
                  <img src={user.response.partnerImage} alt="Partner" />
                ) : (
                  initials
                )}
              </div>
              <div>
                <div className="acName" style={{ margin: 0 }}>
                  {displayName}
                </div>
                <div className="acRole">{ACCOUNT_TYPE_LABEL[accountType] || "Partner account"}</div>
              </div>
            </div>

            <label className="fldLbl">Partner public name</label>
            <p className="fldHelp" style={{ margin: "0 0 12px" }}>
              This name will be displayed on the widget and affiliate UI in the
              fees breakdown section.
            </p>
            <input
              className="input"
              type="text"
              name="partnerPublicName"
              placeholder="Enter your public name"
              value={partnerPublicName}
              required
              onChange={(e) => setPartnerPublicName(e.target.value)}
            />

            <button
              type="button"
              className="btnPrimary block"
              style={{ marginTop: 14, height: 50 }}
              onClick={handleSubmit}
            >
              {isLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <span>Save changes</span>
              )}
            </button>

            {errorMessage && (
              <div className="notice red" style={{ marginTop: 14 }}>
                <span className="ni">⚠</span>
                <div>{errorMessage}</div>
              </div>
            )}
            {successMessage && (
              <div className="notice green" style={{ marginTop: 14 }}>
                <span className="ni">✓</span>
                <div>{successMessage}</div>
              </div>
            )}
          </div>

          <AccountModeSection />

          <TwoFactorSection />
        </div>
      </div>
    </>
  );
};

export default Account;
